/**
 * iter-101 — Theme self-test.
 *
 * Runs once per session. After `applyThemeBg(slug)` fires, this util
 * re-reads the computed `background-image` from <body> and validates:
 *   - The CSS variable `--app-bg-image` resolved to something non-empty.
 *   - The computed background-image is NOT the string "none" (browser
 *     rejected the value silently → iter-97 bug repro).
 *
 * On failure it:
 *   1. Logs to console.error with a clear "[theme-self-test]" tag so
 *      our errorAutoDetect dedupe doesn't swallow it.
 *   2. Reverts to the default preset client-side so the user sees
 *      something instead of a broken blank.
 *   3. Files a support_tickets row via /api/support/chat so the Owner
 *      Dashboard surfaces the regression.
 *
 * Designed as a preemptive bug-catcher — most CSS rejection happens
 * silently in the browser, so without this we'd only learn about it
 * from user complaints.
 */
import axios from "axios";
import { applyThemeBg, BG_PRESETS, DEFAULT_BG } from "@/lib/themeBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_FLAG = "elpm-theme-self-test-fired";

/** Run the self-test on the currently-applied theme. Idempotent per session.
 *  Returns true if the theme rendered correctly, false if we had to revert. */
export async function runThemeSelfTest(currentSlug) {
  if (typeof document === "undefined" || typeof window === "undefined") return true;
  try {
    if (sessionStorage.getItem(SESSION_FLAG)) return true;
    sessionStorage.setItem(SESSION_FLAG, "1");
  } catch (e) { void e; }

  // Force a layout flush so getComputedStyle reflects the new vars.
  // requestAnimationFrame ensures we read after the browser repaints.
  await new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));

  const computedImage = getComputedStyle(document.body).backgroundImage;
  const cssVar = getComputedStyle(document.documentElement).getPropertyValue("--app-bg-image").trim();
  const valid = !!cssVar && computedImage !== "none";

  if (valid) return true;

  // Failed — file a ticket + revert to default.
  console.error(
    "[theme-self-test] CSS rejected the theme background. slug=%s, --app-bg-image=%s, computed=%s",
    currentSlug, cssVar.slice(0, 200), computedImage.slice(0, 200),
  );
  // Revert so the user isn't stuck with a black blank.
  try { applyThemeBg(DEFAULT_BG); } catch (e) { void e; }
  // Fire-and-forget ticket file. The support endpoint is open to anon.
  try {
    const presetNames = BG_PRESETS.map((p) => p.slug).join(", ");
    await axios.post(`${API}/support/chat`, {
      message: `[automated theme self-test] Failed to apply theme "${currentSlug}". CSS variable --app-bg-image was rejected by the browser. Computed background-image = "${computedImage.slice(0, 80)}". Reverted to default. Known presets: ${presetNames}`,
      user_token: localStorage.getItem("elpm-user-token") || "anon",
      context: { component: "themeBg", failed_slug: currentSlug },
    }, { timeout: 8000 });
  } catch (e) { void e; }
  return false;
}
